import { ajaxController } from "./controller/AjaxController";
import { clientMutationOptions } from "./controller/clientMutation";
import { PagedResult } from "../DTOs/Common/PagedResult";
import {
  BlastHoleDTO,
  IssueWorkSheetRequest,
  WorkSheetDTO,
  DrilledHoleSaveResultDTO,
  DrillingEntryMode,
  DrillingSessionHolesDTO,
  HoleDesignImportResultDTO,
  HoleDesignMappingDTO,
  HoleDesignPreviewDTO,
  HoleDipDTO,
  HoleChargeSaveResultDTO,
  HoleDipSaveResultDTO,
  IredesImportResultDTO,
  PatternChargingDTO,
  PatternHolesDTO,
  SaveDrilledHoleRequest,
  SaveHoleChargeRequest,
  SaveHoleDipRequest,
} from "../DTOs/DrillBlast/BlastHoleDTO";

class BlastHolesAjax {
  private _ajaxController = ajaxController;
  private _endpoint = "/BlastHoles";

  public getHoles(patternBlastId: number) {
    return this._ajaxController.get<PatternHolesDTO>(`${this._endpoint}/pattern/${patternBlastId}`, {});
  }

  public addHole(patternBlastId: number, holeName: string, holeTypeId: number, collar: { e: number; n: number } | null) {
    return this._ajaxController.post<{ holeName: string; holeTypeId: number; collarE: number | null; collarN: number | null }, BlastHoleDTO>(
      `${this._endpoint}/pattern/${patternBlastId}`, { holeName, holeTypeId, collarE: collar?.e ?? null, collarN: collar?.n ?? null });
  }

  public getDesignMappings() {
    return this._ajaxController.get<HoleDesignMappingDTO[]>(`${this._endpoint}/design-mappings`, {});
  }

  public previewDesign(patternBlastId: number, text: string) {
    return this._ajaxController.post<{ text: string }, HoleDesignPreviewDTO>(`${this._endpoint}/pattern/${patternBlastId}/design/preview`, { text });
  }

  public importDesign(patternBlastId: number, fileName: string, text: string, mapping: HoleDesignMappingDTO, saveAs: string | null) {
    return this._ajaxController.post<object, HoleDesignImportResultDTO>(`${this._endpoint}/pattern/${patternBlastId}/design`,
      { fileName, text, mapping, saveAs });
  }

  public getSession(drillingBlastId: number) {
    return this._ajaxController.get<DrillingSessionHolesDTO>(`${this._endpoint}/session/${drillingBlastId}`, {});
  }

  public setEntryMode(drillingBlastId: number, entryMode: DrillingEntryMode) {
    return this._ajaxController.put<{ entryMode: DrillingEntryMode }, DrillingSessionHolesDTO>(`${this._endpoint}/session/${drillingBlastId}/entry-mode`,
      { entryMode });
  }

  public addAttempt(request: SaveDrilledHoleRequest, clientId?: string) {
    return this._ajaxController.post<SaveDrilledHoleRequest, DrilledHoleSaveResultDTO>(`${this._endpoint}/attempts`, request, undefined, undefined,
      clientMutationOptions(clientId));
  }

  public updateAttempt(attemptId: number, request: SaveDrilledHoleRequest, clientId?: string) {
    return this._ajaxController.put<SaveDrilledHoleRequest, DrilledHoleSaveResultDTO>(`${this._endpoint}/attempts/${attemptId}`, request, undefined,
      clientMutationOptions(clientId));
  }

  public deleteAttempt(attemptId: number, clientId?: string) {
    return this._ajaxController.delete<boolean>(`${this._endpoint}/attempts/${attemptId}`, clientMutationOptions(clientId));
  }

  public getFieldPatterns() {
    return this._ajaxController.get<PagedResult<number>>(`${this._endpoint}/field-patterns`, {});
  }

  public getDips(patternBlastId: number) {
    return this._ajaxController.get<HoleDipDTO[]>(`${this._endpoint}/pattern/${patternBlastId}/dips`, {});
  }

  public addDip(request: SaveHoleDipRequest, clientId?: string) {
    return this._ajaxController.post<SaveHoleDipRequest, HoleDipSaveResultDTO>(`${this._endpoint}/dips`, request, undefined, undefined,
      clientMutationOptions(clientId));
  }

  public updateDip(dipId: number, request: SaveHoleDipRequest, clientId?: string) {
    return this._ajaxController.put<SaveHoleDipRequest, HoleDipSaveResultDTO>(`${this._endpoint}/dips/${dipId}`, request, undefined,
      clientMutationOptions(clientId));
  }

  public deleteDip(dipId: number, clientId?: string) {
    return this._ajaxController.delete<boolean>(`${this._endpoint}/dips/${dipId}`, clientMutationOptions(clientId));
  }

  public downloadIredesPlan(patternBlastId: number, reference: string) {
    return this._ajaxController.downloadFile(`${this._endpoint}/pattern/${patternBlastId}/iredes-plan`,
      `${reference.replace(/[\\/:*?"<>|]/g, "_")}.xml`);
  }

  public importIredesQuality(drillingBlastId: number, fileName: string, text: string) {
    return this._ajaxController.post<{ fileName: string; text: string }, IredesImportResultDTO>(`${this._endpoint}/session/${drillingBlastId}/iredes-quality`,
      { fileName, text });
  }

  public getSheet(patternBlastId: number, kind: "Drill" | "Dip" | "Loading", workSheetId?: number) {
    return this._ajaxController.get<string>(`${this._endpoint}/pattern/${patternBlastId}/sheets/${kind}`, workSheetId != null ? { workSheetId } : {});
  }

  public issueWorkSheet(patternBlastId: number, request: IssueWorkSheetRequest) {
    return this._ajaxController.post<IssueWorkSheetRequest, WorkSheetDTO>(`${this._endpoint}/pattern/${patternBlastId}/work-sheets`, request);
  }

  public closeWorkSheet(workSheetId: number, rowVersion: string) {
    return this._ajaxController.put<{ rowVersion: string }, WorkSheetDTO>(`${this._endpoint}/work-sheets/${workSheetId}/close`, { rowVersion });
  }

  public getCharging(patternBlastId: number) {
    return this._ajaxController.get<PatternChargingDTO>(`${this._endpoint}/pattern/${patternBlastId}/charging`, {});
  }

  public saveCharge(blastHoleId: number, request: SaveHoleChargeRequest, clientId?: string) {
    return this._ajaxController.put<SaveHoleChargeRequest, HoleChargeSaveResultDTO>(`${this._endpoint}/holes/${blastHoleId}/charge`, request, undefined,
      clientMutationOptions(clientId));
  }
}

export const blastHolesAjax = new BlastHolesAjax();
